import LegalLayout from "@/components/LegalLayout";
import { COMPANY } from "@/lib/content";

export default function PrivacyPage() {
  const sections = [
    {
      heading: "1. Who we are",
      paragraphs: [
        `${COMPANY.name} (\u201cAmanah\u201d, \u201cwe\u201d, \u201cus\u201d) is the data controller for personal information collected through this website. We are registered in England and Wales, company number ${COMPANY.number}, registered office ${COMPANY.office}.`,
        `You can contact us about privacy at ${COMPANY.email}.`,
      ],
    },
    {
      heading: "2. What this notice covers",
      paragraphs: ["This notice explains how we handle personal information submitted through this website via:"],
      list: [
        "Charity consultation requests (Book a Consultation)",
        "Fundraiser-interest submissions (Join Our Fundraising Team)",
        "General enquiries (Contact)",
        "Complaints (Contact)",
      ],
    },
    {
      heading: "3. Information we collect",
      paragraphs: [
        "We collect only the information you choose to provide in our forms. Depending on the form this may include: your name, role, organisation or charity name, charity number, email address, telephone number, operating location or campaign area, the type of support required, your general work area, driving status, relevant experience, availability, and the content of your message.",
        "We ask that you do not submit unnecessary sensitive personal information through this website. We do not collect payment or card details, and this website does not process donations.",
      ],
    },
    {
      heading: "4. How your information is handled when you submit a form",
      paragraphs: [
        "When you submit a form, your details are stored in our website database, and a notification is sent to our monitored inbox using our transactional email provider so that we can respond. We also send you an automated acknowledgement. Simple anti-spam measures (a hidden field check) help protect the forms; we do not use third-party CAPTCHA. Our servers may record limited technical information, including your IP address, for security and to keep the website running safely.",
      ],
    },
    {
      heading: "5. Purposes and lawful bases",
      paragraphs: ["We rely on the following lawful bases under UK GDPR:"],
      list: [
        "Charity consultation and general enquiries \u2014 our legitimate interests in responding to enquiries, developing business relationships and maintaining appropriate records of correspondence.",
        "Complaints \u2014 our legitimate interests in investigating, responding to and learning from complaints, together with our legal obligations where these genuinely apply.",
        "Fundraiser-interest submissions \u2014 taking steps at your request before entering into a possible contract, and our legitimate interests in recruitment and workforce planning.",
        "Website security and administration \u2014 our legitimate interests in protecting and administering the website.",
      ],
    },
    {
      heading: "6. About consent",
      paragraphs: [
        "The acknowledgement box on our forms confirms that you have read this Privacy Notice. It is not a request for consent to the necessary processing described above, which we carry out on the lawful bases set out in section 5. Where we ever offer a genuinely optional activity, we will ask for your consent separately and you will be free to refuse or withdraw it without affecting your enquiry.",
      ],
    },
    {
      heading: "7. Our legitimate interests and your right to object",
      paragraphs: [
        "Where we rely on legitimate interests, we have considered your rights and interests. You can object to processing based on legitimate interests at any time by contacting us; we will stop unless we have compelling legitimate grounds to continue or need to keep the information for a legal claim.",
      ],
    },
    {
      heading: "8. Recipients",
      paragraphs: [
        "We do not sell your personal information. We share it only where necessary with categories of service provider who act for us under appropriate terms, namely: our website hosting and backend infrastructure provider, our database hosting, and our transactional email provider (Resend).",
        "Where a complaint concerns fundraising carried out on behalf of a charity, we may share relevant details with that charity partner where necessary to investigate and respond. We may also disclose information where we are legally required to do so.",
      ],
    },
    {
      heading: "9. International transfers",
      paragraphs: [
        "Some of the service providers we rely on may process personal information outside the United Kingdom.",
      ],
      note: "We are confirming the processing locations of each provider and the specific safeguards (for example the UK International Data Transfer Agreement, an approved addendum to Standard Contractual Clauses, or reliance on UK adequacy) that apply to any transfer outside the UK. This section will be completed and this marker removed before the notice is treated as final. Until then we make no specific transfer-safeguard claim.",
    },
    {
      heading: "10. Retention",
      paragraphs: ["We keep personal information only for as long as necessary:"],
      list: [
        "Charity and general enquiries \u2014 up to 24 months after the last meaningful contact, unless a contractual relationship begins or longer retention is reasonably required for a dispute or legal obligation.",
        "Unsuccessful fundraiser-interest submissions \u2014 up to 12 months after the recruitment activity ends.",
        "Complaints \u2014 24 months after closure, unless longer retention is reasonably necessary for an unresolved dispute, regulatory matter or legal claim.",
        "Records relating to successful clients, contracted fundraisers or employees are governed by the relevant operational retention requirements.",
      ],
    },
    {
      heading: "11. Analytics and cookies",
      paragraphs: [
        "We do not currently use website analytics, advertising pixels, retargeting tools or non-essential tracking cookies. If we introduce privacy-focused analytics in the future, we will review the position and update this notice first. See our Cookie Information page for details of the strictly necessary technologies we use.",
      ],
    },
    {
      heading: "12. Automated decision-making",
      paragraphs: [
        "We do not carry out automated decision-making or profiling that produces legal or similarly significant effects.",
      ],
    },
    {
      heading: "13. Your rights",
      paragraphs: ["Under UK data protection law you have the right to:"],
      list: [
        "Access the personal information we hold about you",
        "Request correction of inaccurate information",
        "Request erasure of your information",
        "Restrict or object to certain processing, including the right to object where we rely on legitimate interests",
        "Request portability of your information",
        "Withdraw any consent you have given, where we rely on consent",
      ],
    },
    {
      heading: "14. Complaints",
      paragraphs: [
        `If you have a concern about how we handle your personal information, please contact us first at ${COMPANY.email}. You also have the right to complain to the Information Commissioner's Office (ICO), the UK supervisory authority, at ico.org.uk.`,
      ],
    },
  ];

  return (
    <LegalLayout
      eyebrow="Legal"
      title="Privacy Notice"
      intro="How Amanah Promotions collects, uses and protects the personal information you provide through this website."
      updated="8 August 2026"
      sections={sections}
    />
  );
}
